import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { api, query } from '../api/client'
import type { ChartResponse } from '../api/types'
import { ChartPickers } from '../components/charts/ChartPickers'
import { ChartTable } from '../components/charts/ChartTable'
import { SnapshotSlider } from '../components/charts/SnapshotSlider'
import { useChartSelection } from '../hooks/useChartSelection'
import { formatDateTime, formatRelative } from '../lib/format'

export function ChartsScreen() {
	const [selection, setSelection] = useChartSelection()
	const [params, setParams] = useSearchParams()
	const at = params.get('at')
	const path = `/api/v1/charts/${selection.platform}/${selection.storefront}/${selection.genre}/${selection.type}`
	const chart = useQuery({
		queryKey: ['chart', path, at],
		queryFn: () => api.get<ChartResponse>(path + query({ at })),
		placeholderData: (prev) => prev,
	})
	const history = useQuery({
		queryKey: ['chart-history', path],
		queryFn: () => api.get<{ capturedAt: string }[]>(`${path}/history`),
	})

	function setAt(value: string | null) {
		const next = new URLSearchParams(params)
		if (value) next.set('at', value)
		else next.delete('at')
		setParams(next, { replace: true })
	}

	const data = chart.data
	return (
		<div className="stack">
			<div className="page-header">
				<div>
					<h1>Charts</h1>
					{data?.snapshot && (
						<p className="muted small" style={{ margin: 0 }}>
							{at
								? `As of ${formatDateTime(data.snapshot.capturedAt)}`
								: `Changed ${formatRelative(data.snapshot.capturedAt)}`}{' '}
							· checked {formatRelative(data.snapshot.lastCheckedAt)}
						</p>
					)}
				</div>
				<SnapshotSlider times={(history.data ?? []).map((h) => h.capturedAt)} value={at} onChange={setAt} />
			</div>
			<ChartPickers value={selection} onChange={setSelection} />
			<div className="card" style={{ padding: 0 }}>
				{chart.isPending && <div className="page-loading">Loading…</div>}
				{chart.isError && <div className="empty">This chart isn’t tracked for that combination.</div>}
				{data && !data.chart.enabled && (
					<div className="empty">
						Apple doesn’t publish this chart (it came back empty repeatedly), so it isn’t polled.
					</div>
				)}
				{data && data.chart.enabled && !data.snapshot && (
					<div className="empty">
						No ranking collected yet{at ? ' at that time' : ''}. It will appear after the next poll.
					</div>
				)}
				{data?.snapshot && <ChartTable entries={data.entries} />}
			</div>
		</div>
	)
}
